import {
	adjustColorBrightness,
	getDarkerColor,
	getLighterColor,
	isColorDark,
} from "../../../utils/colorUtils";
import { drawRoundedRect } from "./canvasUtils";

const SVG_ICONS = {
	refresh:
		"M9.92578 11.707C10.2188 11.707 10.4414 11.6133 10.6055 11.4375L15.4336 6.58594C15.6445 6.38672 15.7266 6.15234 15.7266 5.88281C15.7266 5.625 15.6211 5.36719 15.4336 5.17969L10.6055 0.28125C10.4414 0.09375 10.207 0 9.92578 0C9.39844 0 9 0.421875 9 0.960938C9 1.20703 9.09375 1.44141 9.25781 1.62891L12.3867 4.69922C11.7188 4.58203 11.0391 4.51172 10.3594 4.51172C4.61719 4.51172 0 9.11719 0 14.8594C0 20.6133 4.61719 25.2305 10.3594 25.2305C16.1016 25.2305 20.707 20.6133 20.707 14.8594C20.707 14.2969 20.3086 13.8867 19.7344 13.8867C19.1836 13.8867 18.8203 14.2969 18.8203 14.8594C18.8203 19.5703 15.0586 23.3438 10.3594 23.3438C5.66016 23.3438 1.88672 19.5703 1.88672 14.8594C1.88672 10.1602 5.66016 6.39844 10.3594 6.39844C11.25 6.39844 12.0703 6.46875 12.7734 6.62109L9.26953 10.1016C9.09375 10.2891 9 10.5234 9 10.7695C9 11.3086 9.39844 11.707 9.92578 11.707Z",
	back: "M14.8232 19.1943L8.24072 12.7695C8.08301 12.6118 8 12.4209 8 12.2134C8 11.9976 8.09131 11.79 8.24902 11.6572L14.8232 5.22412C14.9644 5.08301 15.147 5 15.3628 5C15.7944 5 16.1182 5.33203 16.1182 5.76367C16.1182 5.96289 16.0352 6.16211 15.9023 6.30322L9.85938 12.2134L15.9023 18.1235C16.0352 18.2646 16.1182 18.4556 16.1182 18.6631C16.1182 19.0947 15.7944 19.4185 15.3628 19.4185C15.147 19.4185 14.9644 19.3354 14.8232 19.1943Z",
	forward:
		"M9.29395 19.1943L15.8765 12.7695C16.0342 12.6118 16.1172 12.4209 16.1172 12.2134C16.1172 11.9976 16.0259 11.79 15.8682 11.6572L9.29395 5.22412C9.15283 5.08301 8.97021 5 8.75439 5C8.32275 5 7.99902 5.33203 7.99902 5.76367C7.99902 5.96289 8.08203 6.16211 8.21484 6.30322L14.2578 12.2134L8.21484 18.1235C8.08203 18.2646 7.99902 18.4556 7.99902 18.6631C7.99902 19.0947 8.32275 19.4185 8.75439 19.4185C8.97021 19.4185 9.15283 19.3354 9.29395 19.1943Z",
};

export const renderMobileFrame = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number,
	borderRadius: number,
	borderColor: string,
	borderWidth: number,
) => {
	ctx.save();
	ctx.lineWidth = borderWidth;
	ctx.strokeStyle = borderColor;

	// Create a path for the rounded rectangle
	ctx.beginPath();

	drawRoundedRect(ctx, x, y, width, height, borderRadius);

	// Apply the stroke
	ctx.stroke();
	ctx.restore();

	// --- Camera Notch ---
	const notchWidth = Math.max(width * 0.25, 150);
	const notchHeight = height * 0.03;
	const notchX = x + width / 2 - notchWidth / 2;
	const notchY = y - borderWidth / 2;

	ctx.fillStyle = "black";
	ctx.beginPath();
	drawRoundedRect(
		ctx,
		notchX,
		notchY,
		notchWidth,
		notchHeight,
		0,
		0,
		notchHeight / 2,
	);
	ctx.fill();

	// --- Camera Lens ---
	const lensDiameter = notchHeight * 0.5;
	const lensX = notchX + notchWidth * 0.1;
	const lensY = notchY + notchHeight / 2;
	const lensColor = "#0066FF";

	ctx.fillStyle = lensColor;
	ctx.beginPath();
	ctx.arc(lensX, lensY, lensDiameter / 2, 0, 2 * Math.PI);
	ctx.fill();

	// --- Earpiece Speaker ---
	const speakerWidth = notchWidth * 0.3;
	const speakerHeight = notchHeight * 0.15;
	const speakerX = notchX + notchWidth / 2 - speakerWidth / 2;
	const speakerY = notchY + notchHeight / 2 - speakerHeight / 2;

	// Draw the earpiece speaker
	ctx.fillStyle = "#222222";
	ctx.beginPath();
	drawRoundedRect(
		ctx,
		speakerX,
		speakerY,
		speakerWidth,
		speakerHeight,
		speakerHeight / 2,
	);
	ctx.fill();
};

export const renderFrameBorder = (
	ctx: CanvasRenderingContext2D,
	xPos: number,
	yPos: number,
	videoRenderWidth: number,
	videoRenderHeight: number,
	borderWidth: number,
	borderRadius: number,
	color: string,
) => {
	// Calculate the top border thickness (6% of the video height)
	const topThickness = videoRenderHeight * 0.06;

	// Calculate parameters for the outer frame (includes extra space for the border)
	const outerX = xPos - borderWidth;
	const outerY = yPos - topThickness - borderWidth; // Adjusted Y position
	const outerW = videoRenderWidth + borderWidth * 2;

	// Use a larger radius so the outer arcs properly envelop the inner rounded content
	const outerRadius = borderRadius + borderWidth; // Adjusted radius

	ctx.save();

	// Draw the outer frame as a rounded rectangle
	ctx.beginPath();

	drawRoundedRect(
		ctx,
		xPos,
		yPos,
		videoRenderWidth,
		videoRenderHeight,
		borderRadius,
		0,
		borderRadius,
	);

	ctx.strokeStyle = color;
	ctx.lineWidth = borderWidth * 2;
	ctx.stroke();

	ctx.restore();

	ctx.save();

	// Draw the background fill for the top bar only
	ctx.beginPath();

	drawRoundedRect(
		ctx,
		outerX,
		outerY + borderWidth,
		outerW,
		topThickness,
		outerRadius,
		outerRadius,
		0,
	);
	ctx.fillStyle = color;
	ctx.fill();

	ctx.restore();
};

export const renderMacFrame = (
	drawSVGPath: (
		ctx: CanvasRenderingContext2D,
		path: string,
		x: number,
		y: number,
		scale: number,
	) => CanvasRenderingContext2D,
	ctx: CanvasRenderingContext2D,
	xPos: number,
	yPos: number,
	videoRenderWidth: number,
	videoRenderHeight: number,
	borderWidth: number,
	borderRadius: number,
	color: string,
	text: string,
) => {
	const addressBarHeight = videoRenderHeight * 0.04;
	const titleBarHeight = videoRenderHeight * 0.05;

	ctx.save();

	renderFrameBorder(
		ctx,
		xPos,
		yPos,
		videoRenderWidth,
		videoRenderHeight,
		borderWidth,
		borderRadius,
		color,
	);

	renderBrowserControls(
		ctx,
		drawSVGPath,
		{
			x: xPos,
			y: yPos - titleBarHeight,
			width: videoRenderWidth,
			height: addressBarHeight,
			urlBarText: text,
			color: color,
		},
		color,
	);

	ctx.restore();
};

interface BrowserControlsConfig {
	x: number;
	y: number;
	width: number;
	height: number;
	urlBarText: string;
	color: string;
}

export const renderBrowserControls = (
	ctx: CanvasRenderingContext2D,
	drawSVGPath: (
		ctx: CanvasRenderingContext2D,
		path: string,
		x: number,
		y: number,
		scale: number,
	) => CanvasRenderingContext2D,
	config: BrowserControlsConfig,
	color: string,
) => {
	const { x, y, width, height, urlBarText } = config;
	const controlSize = height * 0.45;
	const padding = height * 0.5;
	const cornerRadius = height * 0.5;
	const buttonExpansion = height * 0.5;

	// Page controls (back/forward)
	const pageControlWidth = controlSize * 1.25;
	const safeMargin = width >= 1600 ? width * 0.06 : width * 0.01;
	const spaceBetweenIcons = width * 0.015;
	const pageControlX = x + safeMargin;

	// Calculate traffic light dimensions based on existing variables
	const trafficLightRadius = Math.min(12, height * 0.2);
	const trafficLightSpacing = trafficLightRadius * 3; // Spacing relative to radius
	const trafficLightYOffset = height / 2; // Vertically center traffic lights

	const trafficLightX = x + trafficLightSpacing;
	const startX = trafficLightX;

	// Red light
	ctx.fillStyle = "#EE6A5F";
	ctx.beginPath();
	ctx.arc(
		startX,
		y + trafficLightYOffset,
		trafficLightRadius,
		0,
		2 * Math.PI,
	);
	ctx.fill();

	// Yellow light
	ctx.fillStyle = "#F5BD4F";
	ctx.beginPath();
	ctx.arc(
		startX + trafficLightSpacing,
		y + trafficLightYOffset,
		trafficLightRadius,
		0,
		2 * Math.PI,
	);
	ctx.fill();

	// Green light
	ctx.fillStyle = "#61C454";
	ctx.beginPath();
	ctx.arc(
		startX + trafficLightSpacing * 2,
		y + trafficLightYOffset,
		trafficLightRadius,
		0,
		2 * Math.PI,
	);
	ctx.fill();

	if (width >= 1600) {
		const navigationIcons = [SVG_ICONS.back, SVG_ICONS.forward];

		ctx.save();

		navigationIcons.forEach((path, index) => {
			const buttonX =
				pageControlX +
				index * (pageControlWidth + spaceBetweenIcons / 2);

			// Draw the navigation icon centered inside the button
			ctx.fillStyle = isColorDark(color)
				? getLighterColor(color, 0.5)
				: getDarkerColor(color, 0.35);
			const iconScale = (controlSize / 24) * 1.5;
			const iconOffset = 12 * iconScale;

			drawSVGPath(
				ctx,
				path,
				buttonX + pageControlWidth / 2 - iconOffset,
				y + height / 2 - iconOffset,
				iconScale,
			);
		});

		ctx.restore();
	}

	// Calculate refresh button position and width
	const refreshButtonWidth = pageControlWidth + buttonExpansion;
	const refreshButtonLeft = x + width - refreshButtonWidth - padding;

	const urlBarX =
		width >= 1800
			? pageControlX + (pageControlWidth + 2 * spaceBetweenIcons)
			: width > 1200
				? pageControlX + 2 * (pageControlWidth + 2 * spaceBetweenIcons)
				: pageControlX + 2 * (pageControlWidth + 3 * spaceBetweenIcons);
	const urlBarWidth = refreshButtonLeft - urlBarX - spaceBetweenIcons * 0.5;

	// Using a darker color for the URL bar
	ctx.fillStyle = getDarkerColor(color, 0.35);
	ctx.beginPath();
	drawRoundedRect(ctx, urlBarX, y, urlBarWidth, height, cornerRadius / 2);
	ctx.fill();

	// URL text (Adjust font size based on available height)
	const fontSize = Math.min(height * 0.5, 32);

	// Make sure the text is visible against the darker URL bar
	ctx.fillStyle = getLighterColor(getDarkerColor(color, 0.5), 0.75);
	ctx.font = `600 ${fontSize}px Inter`;
	ctx.textAlign = "left";
	ctx.textBaseline = "middle";

	let clippedUrlBarText = urlBarText;
	while (
		ctx.measureText(clippedUrlBarText).width >
		urlBarWidth - padding * 3
	) {
		clippedUrlBarText = clippedUrlBarText.slice(0, -1);
	}

	ctx.fillText(clippedUrlBarText, urlBarX + padding * 1.5, y + height / 2);

	// Draw refresh icon
	ctx.fillStyle = isColorDark(color)
		? getLighterColor(color, 0.5)
		: getDarkerColor(color, 0.35);
	const iconScale = (controlSize / 24) * 1.5;
	const iconOffset = 12 * iconScale;

	const refreshIconX =
		refreshButtonLeft + refreshButtonWidth / 2 - iconOffset;
	const refreshIconY = y + height / 2 - iconOffset;

	drawSVGPath(ctx, SVG_ICONS.refresh, refreshIconX, refreshIconY, iconScale);
};

export const renderGlassFrame = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number,
	borderWidth: number,
	borderColor: string,
	borderRadius: number,
) => {
	ctx.save();
	ctx.globalAlpha = 0.5;

	// Calculate adjusted values for the border
	const offset = borderWidth / 2;
	const adjustedX = x - offset;
	const adjustedY = y - offset;
	const adjustedWidth = width + 2 * offset;
	const adjustedHeight = height + 2 * offset;

	// Draw the rounded rectangle path
	drawRoundedRect(
		ctx,
		adjustedX,
		adjustedY,
		adjustedWidth,
		adjustedHeight,
		borderRadius === 0 ? 0 : borderRadius + 2 * offset,
	);

	ctx.shadowColor = "rgba(255, 255, 255, 0.1)";
	ctx.shadowBlur = 45;
	ctx.shadowOffsetX = 5;
	ctx.shadowOffsetY = 5;

	// Draw a subtle border
	ctx.strokeStyle = getLighterColor(borderColor, 0.25);
	ctx.lineWidth = borderWidth;
	ctx.stroke();

	ctx.restore();
};

export const renderSolidBorder = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number,
	borderWidth: number,
	borderColor: string,
	borderRadius: number,
	flowDirection = 0, // 0-360 degrees
) => {
	ctx.save();

	// Calculate adjusted values for the border
	const offset = borderWidth / 2;
	const adjustedX = x - offset;
	const adjustedY = y - offset;
	const adjustedWidth = width + 2 * offset;
	const adjustedHeight = height + 2 * offset;

	// Shadow properties for the neon glow
	if (flowDirection > 0) {
		ctx.shadowColor = "rgba(255, 255, 255, 0.75)";
		ctx.shadowBlur = borderWidth * 4;
	}

	// Gradient for flowing effect
	const angle = (flowDirection * Math.PI) / 180;

	// Calculate gradient coordinates
	const centerX = adjustedX + adjustedWidth / 2;
	const centerY = adjustedY + adjustedHeight / 2;

	const startX = centerX + Math.cos(angle) * (adjustedWidth / 2);
	const startY = centerY + Math.sin(angle) * (adjustedHeight / 2);
	const endX = centerX - Math.cos(angle) * (adjustedWidth / 2);
	const endY = centerY - Math.sin(angle) * (adjustedHeight / 2);

	const gradient = ctx.createLinearGradient(startX, startY, endX, endY);

	// Get the base color without opacity
	const baseColor = borderColor ?? "#FFFFFF";

	// Create a flowing gradient pattern
	gradient.addColorStop(0, baseColor);
	gradient.addColorStop(0.3, adjustColorBrightness(baseColor, 1.5)); // Brighter
	gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.75)"); // White-hot center
	gradient.addColorStop(0.7, adjustColorBrightness(baseColor, 1.5)); // Brighter
	gradient.addColorStop(1, baseColor);

	drawRoundedRect(
		ctx,
		adjustedX,
		adjustedY,
		adjustedWidth,
		adjustedHeight,
		borderRadius === 0 ? 0 : borderRadius + 2 * offset,
	);

	ctx.strokeStyle = flowDirection > 0 ? gradient : borderColor;
	ctx.lineWidth = borderWidth;
	ctx.stroke();

	ctx.restore();
};

export const renderAnimatedBorder = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number,
	time: number,
	borderWidth = 4,
	borderColor = "#d43f8c",
	borderRadius = 8,
) => {
	// Calculate the flow direction for the animated border.
	// - Multiplies `time` by 30 to control the speed of animation (30 degrees per unit time).
	// - Uses `% 360` to keep the value within a full rotation (0-360 degrees).
	const flowDirection = (time * 30) % 360;

	renderSolidBorder(
		ctx,
		x,
		y,
		width,
		height,
		borderWidth,
		borderColor,
		borderRadius,
		flowDirection,
	);
};
